import axios from "axios";
import React, { useEffect, useState } from "react";
import { Modal, Button, Col, message, Input, Select, Form, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";

const { TextArea } = Input;
const FormItem = Form.Item;
export const PROMPT =
    "You are given a worksheet title and a set of questions.\n\nPlease create a **brief, structured summary** under the heading `Question-wise Summary`.\n\nThe summary should be grouped into **sections**, where each section starts with a **short one-line description** of what the student is doing or learning.\n\nThen, just **list the questions** like this:\n\n* Q1: [task description or input numbers]\n* Q2: [task description or input numbers]\n\nFor variants, write:\n\n* Q4: [description]\n* Variant: [description]\n\nUse this structure:\n\n---\n\n### Question-wise Summary\n\n**Title: [Worksheet Title]**\n\n---\n\n### Section 1: [One-line description of the goal or type of task]\n\n* Q1:\n* Q2:\n\n---\n\n### Section 2: [New concept or shift in task type]\n\n* Q3:\n* Q4:\n* Variant:\n\n...and so on, until the last question.\n\nDo **not add any extra commentary or explanations**. Keep it short and aligned with the student experience.\n\n---\nQuestion starts here\n\nQuestion-wise Summary\nTitle: ${worksheet_title}\n\nQ1.\n";
export const MODEL = "gemini-3-flash-preview";

const GenerateQuesSummaryFormModal = (props: any) => {
    const { setBlock, value } = props;
    const [visible, setVisible] = useState(false);
    const [model, setModel] = useState(MODEL);
    const [val, setVal] = useState(PROMPT);
    const [data, setData] = useState(null);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(false);
    const [contextLoading, setContextLoading] = useState(false);
    const inputRef = React.useRef<any>();

    useEffect(() => {
        if (!visible) return;
        inputRef.current?.focus();
        if (!context) onOpen();
    }, [visible]);

    const updateVal = (e) => {
        setVal(e);
    };

    const onOpen = async () => {
        setContextLoading(true);
        const options = {
            method: "POST",
            url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/getLLMContext`,
            data: {
                data: {
                    worksheet_id: props.worksheet?.id,
                    block_id: props.block_id,
                    level_id: props.level_id,
                },
            },
        };

        return axios
            .request(options)
            .then(function (response) {
                setContextLoading(false);
                if (!response.data?.success) {
                    message.error("Failed to load LLM context");
                    setContext("Failed");
                    return;
                }
                setContext(response.data?.data);
                return response.data?.data;
            })
            .catch(function (error) {
                setContextLoading(false);
                message.error("Failed to load LLM context");
            });
    };

    const onGenerate = async () => {
        setLoading(true);
        const options = {
            method: "POST",
            url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/quesBlockSummary`,
            data: {
                data: {
                    sys_prompt: val,
                    model,
                    worksheet_title: props.worksheet?.title,
                    context: context ?? (await onOpen()),
                },
            },
        };

        axios
            .request(options)
            .then(function (response) {
                setLoading(false);
                if (!response.data?.success) {
                    message.error("Failed");
                    setData("Failed");
                    return;
                }
                setData(response.data?.data?.summary);
                setBlock(response.data?.data?.summary);
            })
            .catch(function (error) {
                setLoading(false);
                message.error("Failed");
            });
    };

    return (
        <>
            <Button type={"primary"} onClick={() => setVisible(true)}>
                Generate Ques Summary
            </Button>

            <Modal
                title="Generate Question Summary"
                open={visible}
                onOk={() => setVisible(false)}
                onCancel={() => setVisible(false)}
            >
                <Col span={24}>
                    <FormItem style={{ width: "100%" }}>
                        <TextArea
                            value={val}
                            onChange={(e) => updateVal(e.target.value)}
                            ref={inputRef}
                            rows={6}
                        />
                    </FormItem>
                </Col>
                <Col span={24}>
                    <Form.Item label="Model">
                        <Select
                            value={model}
                            onChange={(value) => {
                                setModel(value);
                            }}
                        >
                            {[
                                "gemini-3-flash-preview",
                                "gemini-3-pro-preview",
                                "gemini-2.5-pro",
                                "gemini-2.5-flash",
                                "gemini-2.5-flash-lite-preview-06-17",
                                "gemini-2.0-flash",
                                "gemini-2.0-flash-lite",
                                "gemini-1.5-flash",
                                "gemini-1.5-flash-8b",
                                "gemini-1.5-pro",
                            ].map((i) => (
                                <Select.Option value={i}>{i}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Button
                        type={"primary"}
                        onClick={onGenerate}
                        disabled={loading || contextLoading}
                        loading={loading}
                    >
                        Generate
                    </Button>
                </Col>
                <hr />
                <Col span={24}>
                    {contextLoading && (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <Spin
                                indicator={
                                    <LoadingOutlined
                                        style={{ fontSize: 24 }}
                                        spin
                                    />
                                }
                            />
                            <p style={{ marginTop: 12, color: "#666" }}>
                                Loading LLM context and assets...
                            </p>
                        </div>
                    )}
                    {loading && !contextLoading && (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <Spin
                                indicator={
                                    <LoadingOutlined
                                        style={{ fontSize: 24 }}
                                        spin
                                    />
                                }
                            />
                            <p style={{ marginTop: 12, color: "#666" }}>
                                Generating summary...
                            </p>
                        </div>
                    )}
                    {!loading && !contextLoading && data && (
                        <ReactMarkdown>{data}</ReactMarkdown>
                    )}
                    {!loading && !contextLoading && !data && value && (
                        <ReactMarkdown>{value}</ReactMarkdown>
                    )}
                </Col>
            </Modal>
        </>
    );
};

export default GenerateQuesSummaryFormModal;
